import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { CotacaoDTO } from '../../../models/gerenciar-contas/cotacao.model';
import { CotacaoService } from '../../../services/cotacao/cotacao.service';
import { EmpresaConfigService } from '../../../services/configuracoes/empresa.service';
import { Moeda, MOEDA_CONFIG } from '../../../enums/moeda.enum';
import { Subscription } from 'rxjs';
import { toLocalISOString } from '../../../shared/utils/date-utils';
import { CampoCotacao, camposCotacaoVisiveis } from '../../../utils/moeda-pareamento.util';

@Component({
  selector: 'app-cotacao-edit',
  templateUrl: './cotacao-edit.component.html',
  styleUrls: ['./cotacao-edit.component.scss'],
  providers: [MessageService],
})
export class CotacaoEditComponent implements OnInit, OnDestroy {
  form: CotacaoDTO = this.createEmptyForm();
  idEdit?: number;

  /** Moeda padrao da empresa logada (usada para filtrar os pares de cotacao visiveis). */
  private moedaEmpresa: Moeda | null = null;
  /** Campos de cotacao visiveis na UI e obrigatorios no submit (derivados da moeda da empresa). */
  camposVisiveis: CampoCotacao[] = camposCotacaoVisiveis(null);

  get moedaPadraoLabel(): string {
    const moeda = this.form.moedaPadrao;
    if (!moeda) return '';
    // Se for string do enum, buscar label legível
    const moedaStr = (typeof moeda === 'object' ? (moeda as any).nome || (moeda as any).value : moeda) as string;
    const config = MOEDA_CONFIG[moedaStr as Moeda];
    return config ? config.label : moedaStr;
  }

  // Objetos de traducao
  texts = {
    titulo: '',
    subtitulo: '',
    editar_cotacao: '',
    moeda_padrao: '',
    dolar_guarani: '',
    real_guarani: '',
    dolar_real: '',
    peso_guarani: ''
  };

  placeholders = {
    selecione: '',
    digite_o_valor: '',
    peso_guarani: ''
  };

  botoes = {
    salvar: '',
    cancelar: ''
  };

  private langChangeSub!: Subscription;

  constructor(
    private cotacaoService: CotacaoService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private empresaService: EmpresaConfigService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
    this.loadMoedaEmpresa();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.idEdit = +id;
      this.loadCotacao(this.idEdit);
    }
  }

  /**
   * Obtem a moeda padrao da empresa logada (GET /configuracaoEmpresa/sessao)
   * para determinar quais pares de cotacao devem ser exibidos e validados.
   * Em caso de falha, mantem o fallback (trio tradicional).
   */
  private loadMoedaEmpresa(): void {
    this.empresaService.getSessao().subscribe({
      next: (config: any) => {
        const enumStr = config?.moeda?.enum as string | undefined;
        this.moedaEmpresa = enumStr ? (enumStr as Moeda) : null;
        this.camposVisiveis = camposCotacaoVisiveis(this.moedaEmpresa);
      },
      error: () => {
        // Fallback: trio tradicional (comportamento historico).
        this.moedaEmpresa = null;
        this.camposVisiveis = camposCotacaoVisiveis(null);
      },
    });
  }

  /** Indica se um campo de cotacao deve ser exibido/validado no form atual. */
  isVisivel(campo: CampoCotacao): boolean {
    return this.camposVisiveis.includes(campo);
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('cotacao-edit.texts.titulo'),
      subtitulo: this.translate.instant('cotacao-edit.texts.subtitulo'),
      editar_cotacao: this.translate.instant('cotacao-edit.texts.editar_cotacao'),
      moeda_padrao: this.translate.instant('cotacao-edit.labels.moeda_padrao'),
      dolar_guarani: this.translate.instant('cotacao-edit.labels.dolar_guarani'),
      real_guarani: this.translate.instant('cotacao-edit.labels.real_guarani'),
      dolar_real: this.translate.instant('cotacao-edit.labels.dolar_real'),
      peso_guarani: this.translate.instant('cotacao-edit.labels.peso_guarani')
    };

    this.placeholders = {
      selecione: this.translate.instant('cotacao-edit.placeholders.selecione'),
      digite_o_valor: this.translate.instant('cotacao-edit.placeholders.digite_o_valor'),
      peso_guarani: this.translate.instant('cotacao-edit.placeholders.peso_guarani')
    };

    this.botoes = {
      salvar: this.translate.instant('cotacao-edit.botoes.salvar'),
      cancelar: this.translate.instant('cotacao-edit.botoes.cancelar')
    };
  }

  voltar(): void {
    this.router.navigate(['/cotacao']);
  }

  loadCotacao(id: number): void {
    this.cotacaoService.getById(`/cotacao/${id}`).subscribe({
      next: (response) => {
        this.form = {
          ...response,
          // Converte null -> undefined para manter o input numerico vazio
          pesoGuarani: response.pesoGuarani ?? undefined,
        };
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('cotacao.mensagens.erro_carregar'),
        });
        this.router.navigate(['/cotacoes']);
      },
    });
  }

  submit(): void {
    if (!this.validateForm()) {
      return;
    }

    // Monta o body respeitando a regra refinada (abril/2026):
    // - Trio tradicional (dolarGuarani/realGuarani/dolarReal) sempre
    //   presente no payload porque o backend rejeita nulos.
    //   Para empresas cuja moeda padrao nao exibe o trio (ex.: PESO),
    //   o valor vem do proprio form (carregado em loadCotacao a partir
    //   da cotacao persistida). Fallback = 1 para evitar zero/null.
    // - pesoGuarani permanece opcional e so e enviado quando visivel e > 0.
    const { dolarGuarani, realGuarani, dolarReal, pesoGuarani, ...rest } = this.form;
    const body: CotacaoDTO = {
      ...rest,
      atualizadoEm: toLocalISOString(), // Atualiza o campo de modificacao
      dolarGuarani: this.resolverCampoObrigatorio(dolarGuarani),
      realGuarani:  this.resolverCampoObrigatorio(realGuarani),
      dolarReal:    this.resolverCampoObrigatorio(dolarReal),
    };
    if (
      this.isVisivel('pesoGuarani')
      && pesoGuarani != null
      && !Number.isNaN(pesoGuarani)
      && pesoGuarani > 0
    ) {
      body.pesoGuarani = pesoGuarani;
    }

    this.cotacaoService.put(`/cotacao/${this.idEdit}`, body).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('cotacao-edit.mensagens.cotacao_editada'),
        });
        this.router.navigate(['/cotacao']);
      },
      error: (err) => {
        const detalhe = this.resolverMensagemErro(err, 'cotacao-edit.mensagens.falha_editar');
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: detalhe,
        });
      },
    });
  }

  validateForm(): boolean {
    // requiredFields monta-se dinamicamente a partir dos campos visiveis.
    // Empresas GUARANI/REAL/DOLAR: trio tradicional obrigatorio.
    // Empresas PESO: apenas pesoGuarani obrigatorio (> 0).
    // moedaPadrao continua obrigatorio em qualquer cenario.
    const requiredFields: (keyof CotacaoDTO)[] = ['moedaPadrao'];
    if (this.isVisivel('dolarGuarani')) requiredFields.push('dolarGuarani');
    if (this.isVisivel('realGuarani')) requiredFields.push('realGuarani');
    if (this.isVisivel('dolarReal')) requiredFields.push('dolarReal');

    for (const field of requiredFields) {
      if (!this.form[field]) {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: `O campo ${field} é obrigatório.`,
        });
        return false;
      }
    }

    // Quando a empresa opera com PESO, pesoGuarani e obrigatorio (> 0).
    if (this.isVisivel('pesoGuarani')) {
      const peso = this.form.pesoGuarani;
      if (peso == null || Number.isNaN(peso) || peso <= 0) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('common.atencao'),
          detail: this.translate.instant('erros.peso_guarani_invalido'),
        });
        return false;
      }
    } else {
      // Em empresas que NAO operam com PESO, pesoGuarani nao e editavel;
      // mas se por algum motivo o form trouxer valor invalido (<= 0),
      // avisa — o payload sera omitido de qualquer forma.
      const peso = this.form.pesoGuarani;
      if (peso != null && !Number.isNaN(peso) && peso <= 0) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('common.atencao'),
          detail: this.translate.instant('erros.peso_guarani_invalido'),
        });
        return false;
      }
    }

    return true;
  }

  /**
   * Resolve a mensagem de erro a ser exibida no toast.
   * Prioriza: (1) chave i18n derivada do campo `code`, (2) `message` do
   * backend, (3) fallback i18n padrao da tela.
   */
  private resolverMensagemErro(err: any, fallbackKey: string): string {
    const codigo: string | undefined = err?.error?.code;
    if (codigo) {
      const chave = `erros.${codigo}`;
      const traduzida = this.translate.instant(chave);
      // Se o translate devolveu a chave (nao traduziu), cai no proximo fallback.
      if (traduzida && traduzida !== chave) {
        return traduzida;
      }
    }
    const mensagemBackend: string | undefined = err?.error?.message;
    if (mensagemBackend) {
      return mensagemBackend;
    }
    return this.translate.instant(fallbackKey);
  }

  /**
   * Resolve o valor de um campo tradicional de cotacao (dolarGuarani,
   * realGuarani ou dolarReal) para envio obrigatorio ao backend — mesmo
   * quando o campo nao e editavel na UI (ex.: empresa PESO).
   *
   * Ordem de preferencia: valor informado (se > 0), senao fallback = 1.
   */
  private resolverCampoObrigatorio(valor: number | null | undefined): number {
    if (valor != null && !Number.isNaN(valor) && valor > 0) {
      return valor;
    }
    return 1;
  }

  private createEmptyForm(): CotacaoDTO {
    return {
      moedaPadrao: '',
      dolarGuarani: 0,
      realGuarani: 0,
      dolarReal: 0,
      emissao: '',
    };
  }
}
